import { useCallback, useEffect, useMemo, useState } from 'react';
import { Settings2 } from 'lucide-react';
import { api } from '../api/client';
import { useSocle } from '../hooks/useSocle';
import { fmtCHF, fmtCHF2 } from '../utils/format';
import {
  GrilleRevenus, LigneFlux, MOIS_LABELS, GROUPES_FLUX_LABELS,
} from '../types';
import { FluxModal } from '../components/FluxModal';
import { EditionRapideRevenu } from '../components/EditionRapideRevenu';
import { ScenariosModal } from '../components/ScenariosModal';
import { SelectAnnee } from '../components/SelectAnnee';

type Affichage = 'montant' | 'quantite' | 'tarif';

const ORDRE_GROUPES = ['DIRIS', 'OAI', 'SISP', 'EVAM', 'AUTRES'];

export function RevenusPage() {
  const maintenant = new Date();
  const [annee, setAnnee] = useState(maintenant.getFullYear());
  // null = décisions seules (tronc définitif) ; sinon id du scénario affiché
  // undefined = socle pas encore connu ; la grille attend avant de charger
  const socleId = useSocle();
  const [scenarioId, setScenarioId] = useState<number | null | undefined>(undefined);
  const [affichage, setAffichage] = useState<Affichage>('montant');
  const [filtreGroupe, setFiltreGroupe] = useState<string>('tous');
  const [grille, setGrille] = useState<GrilleRevenus | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  const [fluxOuvert, setFluxOuvert] = useState<{ id: number; mois?: number } | null>(null);
  const [scenariosOuverts, setScenariosOuverts] = useState(false);
  const [editionRapide, setEditionRapide] = useState<{
    id: number;
    mois: number;
    ancre: { top: number; bottom: number; left: number; right: number };
  } | null>(null);

  const chargerGrille = useCallback(() => {
    if (scenarioId === undefined) return; // on attend de connaître le socle
    setErreur(null);
    api.get<GrilleRevenus>(`/revenus/grille?annee=${annee}&scenario=${scenarioId ?? 'definitif'}`)
      .then(setGrille)
      .catch((e) => setErreur(e.message))
      .finally(() => setChargement(false));
  }, [annee, scenarioId]);

  useEffect(() => {
    if (scenarioId === undefined && socleId !== undefined) setScenarioId(socleId);
  }, [socleId, scenarioId]);

  useEffect(() => { chargerGrille(); }, [chargerGrille]);

  // Si le scénario affiché a été supprimé ou archivé, revenir au socle (le
  // budget) — et aux décisions seules seulement si c'est le socle qui a disparu
  useEffect(() => {
    if (grille && scenarioId != null && !grille.scenarios.some((s) => s.id === scenarioId && !s.archive)) {
      setScenarioId(scenarioId === socleId ? null : socleId ?? null);
    }
  }, [grille, scenarioId, socleId]);

  const scenariosActifs = useMemo(
    () => (grille?.scenarios ?? []).filter((s) => !s.archive),
    [grille]
  );
  const variantes = useMemo(() => scenariosActifs.filter((s) => s.id !== socleId), [scenariosActifs, socleId]);
  const scenarioNom = scenarioId != null
    ? scenariosActifs.find((s) => s.id === scenarioId)?.nom ?? null
    : null;

  // Mois courant (1..12) si l'année affichée est l'année en cours, sinon 0/13
  const moisCourant = annee === maintenant.getFullYear()
    ? maintenant.getMonth() + 1
    : annee < maintenant.getFullYear() ? 13 : 0;

  const groupesPresents = useMemo(() => {
    if (!grille) return [];
    const presents = new Set(grille.flux.map((f) => f.groupe));
    return ORDRE_GROUPES.filter((g) => presents.has(g));
  }, [grille]);

  const fluxFiltres = useMemo(() => {
    if (!grille) return [];
    return grille.flux.filter((f) => filtreGroupe === 'tous' || f.groupe === filtreGroupe);
  }, [grille, filtreGroupe]);

  // Totaux sur la sélection filtrée
  const totaux = useMemo(() => {
    const parMois = new Array(12).fill(0);
    for (const f of fluxFiltres) {
      for (let m = 0; m < 12; m++) parMois[m] += f.mois[m].montant;
    }
    return { parMois, annuel: parMois.reduce((s, v) => s + v, 0) };
  }, [fluxFiltres]);

  const kpis = useMemo(() => {
    const passe = totaux.parMois.slice(0, Math.max(0, moisCourant - 1)).reduce((s, v) => s + v, 0);
    const courant = moisCourant >= 1 && moisCourant <= 12 ? totaux.parMois[moisCourant - 1] : 0;
    const futur = moisCourant <= 12 ? totaux.parMois.slice(moisCourant).reduce((s, v) => s + v, 0) : 0;
    return { passe, courant, futur, total: totaux.annuel };
  }, [totaux, moisCourant]);

  const classesColonne = (m: number) =>
    m < moisCourant ? ' col-passe' : m === moisCourant ? ' col-courant' : '';

  function ouvrirEdition(ligne: LigneFlux, m: number, ev: React.MouseEvent) {
    if (ligne.mode === 'libre' || ligne.mode === 'convention') {
      // Recettes libres : saisie dans la fiche ; flux dérivés d'une
      // convention : les valeurs se modifient dans le module Contrats.
      setFluxOuvert({ id: ligne.id, mois: m });
      return;
    }
    const r = (ev.currentTarget as HTMLElement).getBoundingClientRect();
    setEditionRapide({
      id: ligne.id, mois: m,
      ancre: { top: r.top, bottom: r.bottom, left: r.left, right: r.right },
    });
  }

  function renduCellule(ligne: LigneFlux, m: number) {
    const c = ligne.mois[m - 1];
    const projete = c.tagMontant === 'projete' || c.tagQuantite === 'projete' || c.tagTarif === 'projete';
    const changement = c.changementMontant ?? c.changementQuantite ?? c.changementTarif;
    const estQT = ligne.mode === 'quantite_tarif';
    // Mois futur dont la valeur n'est qu'un report d'un événement antérieur :
    // c'est une prévision, pas un définitif
    const moisIso = `${annee}-${String(m).padStart(2, '0')}`;
    // Le tarif horaire (coaching OAI) ne dérive rien : c'est le montant qui
    // porte la valeur de la cellule, comme pour un flux en montant direct.
    const moisEffet = estQT && !c.tarifHoraire ? c.moisEffetQuantite : c.moisEffetMontant;
    const previsionReportee = !projete && !c.derive && !c.montantReel &&
      m > moisCourant && moisEffet != null && moisEffet.slice(0, 7) < moisIso;

    const infobulle = [
      `${ligne.libelle} — ${MOIS_LABELS[m - 1]} ${annee}`,
      estQT
        ? c.tarifHoraire
          ? `${c.quantite ?? 0} assurés — CHF ${fmtCHF2(c.montant)}`
            + ` (tarif CHF ${fmtCHF2(c.tarif ?? 0)} /heure : le montant dépend du temps passé, pas du nombre d’assurés)`
          : c.montantReel
            ? `${c.quantite ?? 0} assurés — facturé réel CHF ${fmtCHF2(c.montant)}`
            : `${c.quantite ?? 0} × CHF ${fmtCHF2(c.tarif ?? 0)} = CHF ${fmtCHF2(c.montant)}`
        : `CHF ${fmtCHF2(c.montant)}`,
      c.derive || c.montantReel ? c.source : null,
      // `derive` est posé par trois chemins (convention, reprise de calendrier,
      // annualisation) : seul le mode convention se corrige dans Contrats. Les
      // deux autres se corrigent ici, ce que dit déjà `source`.
      c.derive && ligne.mode === 'convention'
        ? 'Montant dérivé de la convention — modifier le terme annuel dans Contrats' : null,
      previsionReportee ? 'Prévision : report de la dernière valeur en vigueur (aucune décision ne prend effet ce mois-ci)' : null,
      ligne.mode === 'libre' && c.recettes?.length
        ? c.recettes.map((r) => `· ${r.libelle} : CHF ${fmtCHF2(r.montant)}`).join('\n')
        : null,
      c.changementMontant ? `Changement de montant (${c.changementMontant.tag}) : ${c.changementMontant.commentaire}` : null,
      c.changementQuantite ? `Changement de quantité (${c.changementQuantite.tag}) : ${c.changementQuantite.commentaire}` : null,
      c.changementTarif ? `Changement de tarif (${c.changementTarif.tag}) : ${c.changementTarif.commentaire}` : null,
    ].filter(Boolean).join('\n');

    const principal = affichage === 'montant'
      ? (c.montant !== 0 ? fmtCHF(c.montant) : '—')
      : affichage === 'quantite'
        ? (estQT && (c.quantite ?? 0) !== 0 ? String(c.quantite) : '—')
        : (estQT && (c.tarif ?? 0) !== 0 ? `${fmtCHF(c.tarif!)}${c.tarifHoraire ? '/h' : ''}` : '—');
    const secondaire = affichage === 'montant'
      ? (estQT && c.montant !== 0
          ? (c.tarifHoraire ? `${c.quantite} assurés${c.montantReel ? ' · réel' : ''}`
            : c.montantReel ? `${c.quantite} assurés · réel` : `${c.quantite} × ${fmtCHF(c.tarif ?? 0)}`)
        : ligne.mode === 'libre' && (c.recettes?.length ?? 0) > 1 ? `${c.recettes!.length} recettes` : '')
      : (estQT && c.montant !== 0 ? fmtCHF(c.montant) : '');

    return (
      <td
        key={m}
        className={`cell-mois${classesColonne(m)}${projete ? ' cell-projete' : ''}${c.montant === 0 ? ' cell-vide' : ''}${c.derive ? ' cell-derive' : ''}${previsionReportee ? ' cell-report' : ''}`}
        title={infobulle}
        onDoubleClick={(ev) => ouvrirEdition(ligne, m, ev)}
      >
        {changement && <span className={`cell-flag cell-flag--${changement.tag === 'projete' ? 'projete' : 'definitif'}`} />}
        <span className="cell-principal">{principal}</span>
        {secondaire && <span className="cell-secondaire">{secondaire}</span>}
      </td>
    );
  }

  if (chargement) {
    return <div className="flex items-center justify-center" style={{ height: 240 }}><div className="spinner" /></div>;
  }

  const sousTotalGroupe = (groupe: string) =>
    grille?.sousTotaux.find((s) => s.groupe === groupe) ?? null;

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center gap-8">
          <h1 className="page-title">Revenus</h1>
          <span className="stat-pill">CHF {fmtCHF(totaux.annuel)} / {annee}</span>
          {scenarioId == null
            ? <span className="stat-pill" style={{ color: 'var(--ink-2)' }}
                title="Décisions arrêtées seulement — les mois à venir n'y sont qu'un report">décidé seulement</span>
            : scenarioId === socleId
              ? <span className="stat-pill" style={{ color: 'var(--accent)' }}
                  title="Le budget en l'état : décisions arrêtées plus hypothèses retenues">budget</span>
              : <span className="stat-pill" style={{ color: 'var(--warn)' }}>variante · {scenarioNom}</span>}
        </div>
        <div className="flex gap-8 items-center">
          <SelectAnnee annee={annee} onChange={setAnnee} />
        </div>
      </div>

      {erreur && <div className="card mb-16 p-16 text-err">{erreur}</div>}

      <div className="kpi-row mb-16">
        <div className="kpi-card">
          <span className="kpi-card__label">Facturé à ce jour{moisCourant >= 2 && moisCourant <= 13 ? ` (jan – ${MOIS_LABELS[Math.min(moisCourant, 13) - 2].toLowerCase()})` : ''}</span>
          <span className="kpi-card__value">{fmtCHF(kpis.passe)}<span className="kpi-card__suffix">CHF</span></span>
        </div>
        <div className="kpi-card kpi-card--expanded">
          <span className="kpi-card__label">Mois en cours{moisCourant >= 1 && moisCourant <= 12 ? ` (${MOIS_LABELS[moisCourant - 1].toLowerCase()})` : ''}</span>
          <span className="kpi-card__value">{fmtCHF(kpis.courant)}<span className="kpi-card__suffix">CHF</span></span>
        </div>
        <div className="kpi-card">
          <span className="kpi-card__label">À facturer (reste de l'année)</span>
          <span className="kpi-card__value">{fmtCHF(kpis.futur)}<span className="kpi-card__suffix">CHF</span></span>
        </div>
        <div className="kpi-card">
          <span className="kpi-card__label">Total {annee}</span>
          <span className="kpi-card__value">{fmtCHF(kpis.total)}<span className="kpi-card__suffix">CHF</span></span>
        </div>
      </div>

      <div className="toolbar">
        <div className="filter-chips">
          <button className={`chip${filtreGroupe === 'tous' ? ' active' : ''}`} onClick={() => setFiltreGroupe('tous')}>Tous</button>
          {groupesPresents.map((g) => (
            <button key={g} className={`chip${filtreGroupe === g ? ' active' : ''}`} onClick={() => setFiltreGroupe(g)}>
              {GROUPES_FLUX_LABELS[g] ?? g}
            </button>
          ))}
        </div>

        <div className="flex gap-8 items-center" title="Décisions prises seules, ou avec les hypothèses d'un scénario">
          <div className="selectable-group">
            <button
              className={`selectable${scenarioId != null && scenarioId === socleId ? ' active' : ''}`}
              disabled={socleId == null}
              title="Le budget en l'état : décisions arrêtées plus hypothèses retenues"
              onClick={() => setScenarioId(socleId ?? null)}
              >
                Budget
              </button>
              <button
              className={`selectable${scenarioId != null && scenarioId !== socleId ? ' active' : ''}`}
              disabled={variantes.length === 0}
              title={variantes.length === 0 ? 'Aucune variante — en créer une dans Scénarios' : 'Une variante du budget'}
              onClick={() => setScenarioId(variantes[0]?.id ?? null)}
              >
                Variante
              </button>
              <button
              className={`selectable${scenarioId == null ? ' active' : ''}`}
              title="Décisions arrêtées seulement, sans aucune hypothèse — les mois à venir n'y sont qu'un report"
              onClick={() => setScenarioId(null)}
              >
                Décidé seulement
              </button>
          </div>
          {scenarioId != null && scenarioId !== socleId && (
            <select className="sort-select" value={scenarioId} onChange={(e) => setScenarioId(Number(e.target.value))}>
              {variantes.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
          )}
          <button className="btn btn-icon" title="Gérer les scénarios" onClick={() => setScenariosOuverts(true)}>
            <Settings2 className="icon-sm" />
          </button>
        </div>

        <div className="selectable-group" title="Valeur affichée dans les cellules (quantité et tarif : flux OAI)">
          <button className={`selectable${affichage === 'montant' ? ' active' : ''}`} onClick={() => setAffichage('montant')}>Montant</button>
          <button className={`selectable${affichage === 'quantite' ? ' active' : ''}`} onClick={() => setAffichage('quantite')}>Quantité</button>
          <button className={`selectable${affichage === 'tarif' ? ' active' : ''}`} onClick={() => setAffichage('tarif')}>Tarif</button>
        </div>
      </div>

      <div className="grille-container">
        <table className="table table-compact grille-salaires">
          <thead>
            <tr>
              <th className="cell-nom">Flux de revenu</th>
              {MOIS_LABELS.map((label, i) => (
                <th key={label} className={`cell-mois${classesColonne(i + 1)}`}>
                  {label}
                  {i + 1 === moisCourant && <span className="marqueur-courant" title="Mois en cours" />}
                </th>
              ))}
              <th className="cell-total">Total {annee}</th>
            </tr>
          </thead>
          <tbody>
            {groupesPresents
              .filter((g) => filtreGroupe === 'tous' || g === filtreGroupe)
              .map((groupe) => {
                const fluxDuGroupe = fluxFiltres.filter((f) => f.groupe === groupe);
                const st = sousTotalGroupe(groupe);
                return [
                  <tr key={`groupe-${groupe}`} className="ligne-groupe">
                    <td className="cell-nom">{GROUPES_FLUX_LABELS[groupe] ?? groupe}</td>
                    {Array.from({ length: 12 }, (_, i) => (
                      <td key={i} className={`cell-mois${classesColonne(i + 1)}`}>
                        <span className="cell-secondaire">{st && st.parMois[i] !== 0 ? fmtCHF(st.parMois[i]) : ''}</span>
                      </td>
                    ))}
                    <td className="cell-total">
                      <span className="cell-secondaire">{st && st.annuel !== 0 ? fmtCHF(st.annuel) : ''}</span>
                    </td>
                  </tr>,
                  ...fluxDuGroupe.map((ligne) => (
                    <tr key={ligne.id} className="ligne-employe">
                      <td className="cell-nom">
                        <span className="nom-employe" style={{ paddingLeft: 12 }} onClick={() => setFluxOuvert({ id: ligne.id })}
                          title={[ligne.conventionne ? 'Couvert par une convention — voir le risque de rétrocession' : null, ligne.note]
                            .filter(Boolean).join(' · ') || undefined}>
                          {ligne.libelle}
                        </span>
                        {ligne.aProjections && <span className="badge badge-warn badge-sm" title="Comporte des valeurs projetées">proj.</span>}
                      </td>
                      {Array.from({ length: 12 }, (_, i) => renduCellule(ligne, i + 1))}
                      <td className="cell-total">
                        <span className="cell-principal">{ligne.totalAnnuel !== 0 ? fmtCHF(ligne.totalAnnuel) : '—'}</span>
                      </td>
                    </tr>
                  )),
                ];
              })}
            {fluxFiltres.length === 0 && (
              <tr><td colSpan={14} className="text-center text-ink-3 p-16">Aucun flux pour ce filtre.</td></tr>
            )}
          </tbody>
          <tfoot>
            <tr className="ligne-totaux">
              <td className="cell-nom">Total produits</td>
              {totaux.parMois.map((t, i) => (
                <td key={i} className={`cell-mois${classesColonne(i + 1)}`}>
                  <span className="cell-principal">{fmtCHF(t)}</span>
                </td>
              ))}
              <td className="cell-total"><span className="cell-principal">{fmtCHF(totaux.annuel)}</span></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <p className="text-xs text-ink-3 mt-8">
        Montants facturés en CHF (vue facturation — l'encaissement peut suivre d'un mois pour l'OAI, d'un trimestre pour les
        conventions DIRIS). Les valeurs <span className="cell-projete">projetées</span> apparaissent en italique ; un point de
        couleur marque un changement prenant effet ce mois-là (<span className="cell-flag cell-flag--definitif cell-flag--demo" /> définitif,
        {' '}<span className="cell-flag cell-flag--projete cell-flag--demo" /> projeté). Les cellules sur fond hachuré sont
        <strong> calculées</strong> — leur provenance exacte est dans l'infobulle. Les flux conventionnés (DIRIS) sont
        facturés <strong>au mois-place décompté</strong> (module <a href="#mesures" className="text-accent">Mesures</a>) pour les
        mois déjà facturés, puis le <strong>solde du plafond</strong> (prix × places du terme annuel) est réparti sur les mois
        restants : le total annuel égale le plafond, à modifier dans Contrats. Les tarifs
        OAI font foi dans la convention cadre (Contrats), les quantités se saisissent ici. Une rétrocession estimée
        (suivi dans <a href="#mesures" className="text-accent">Mesures</a>) est déduite de la prévision de décembre du
        flux conventionné concerné.
        <strong> Double-cliquer sur une cellule</strong> pour l'édition rapide ; cliquer sur le nom du flux pour le détail complet
        (historique, mandant &amp; contrat, recettes libres, paramètres).
      </p>

      {fluxOuvert && grille && (
        <FluxModal
          fluxId={fluxOuvert.id}
          annee={annee}
          moisInitial={fluxOuvert.mois ?? Math.min(Math.max(moisCourant, 1), 12)}
          ligne={grille.flux.find((f) => f.id === fluxOuvert.id) ?? null}
          scenarios={grille.scenarios}
          scenarioCourant={scenarioId ?? null}
          onFermer={() => setFluxOuvert(null)}
          onModifie={chargerGrille}
        />
      )}
      {scenariosOuverts && (
        <ScenariosModal
          onFermer={() => setScenariosOuverts(false)}
          onModifie={chargerGrille}
        />
      )}
      {editionRapide && grille && (() => {
        const l = grille.flux.find((f) => f.id === editionRapide.id);
        return l ? (
          <EditionRapideRevenu
            ligne={l}
            mois={editionRapide.mois}
            annee={annee}
            scenarios={grille.scenarios}
            scenarioCourant={scenarioId ?? null}
            ancre={editionRapide.ancre}
            onFermer={() => setEditionRapide(null)}
            onModifie={chargerGrille}
            onOuvrirFiche={() => {
              setFluxOuvert({ id: editionRapide.id, mois: editionRapide.mois });
              setEditionRapide(null);
            }}
          />
        ) : null;
      })()}
    </div>
  );
}
